(() => {
  const clampQuantity = value => Math.max(0, Math.min(99, Number.parseInt(value, 10) || 0));

  function initColorTour(root) {
    if (!root || root.dataset.hoColorTourReady === 'true') return;
    root.dataset.hoColorTourReady = 'true';

    const steps = [...root.querySelectorAll('[data-ho-tour-step]')];
    const progressSteps = [...root.querySelectorAll('[data-ho-progress-step]')];
    const progressButtons = [...root.querySelectorAll('[data-ho-go-step]')];
    const quantityInputs = [...root.querySelectorAll('[data-ho-qty-input]')];
    const familyButtons = [...root.querySelectorAll('[data-ho-color-family]')];
    const familyGroups = [...root.querySelectorAll('[data-ho-color-group]')];
    const search = root.querySelector('[data-ho-color-search]');
    const searchStatus = root.querySelector('[data-ho-search-status]');
    const backButton = root.querySelector('[data-ho-back]');
    const nextButton = root.querySelector('[data-ho-next]');
    const addAllButton = root.querySelector('[data-ho-add-all]');
    const chooseSetButton = root.querySelector('[data-ho-choose-set]');
    const skipButtons = [...root.querySelectorAll('[data-ho-skip-step]')];
    const developerWarning = root.querySelector('[data-ho-developer-warning]');
    const confirmNoDeveloperButton = root.querySelector('[data-ho-confirm-no-developer]');
    const returnDeveloperButton = root.querySelector('[data-ho-return-developer]');
    const progress = root.querySelector('[data-ho-tour-progress]');
    const summary = root.querySelector('[data-ho-selection-summary]');
    const cartStatus = root.querySelector('[data-ho-cart-status]');
    const total = root.querySelector('[data-ho-selection-total]');
    const disclosure = root.querySelector('[data-ho-tour-disclosure]');
    const checkout = root.querySelector('.ho-color-system__checkout');
    const currency = root.dataset.currency || 'EUR';
    let currentStep = 1;
    let currentFamily = familyButtons.find(button => button.getAttribute('aria-selected') === 'true')?.dataset.hoColorFamily || 'natural';
    let developerWarningAccepted = false;
    let tourStartSignalled = false;

    const formatMoney = cents => new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(cents / 100);

    function signalTourStart() {
      if (tourStartSignalled) return;
      tourStartSignalled = true;
      document.dispatchEvent(new CustomEvent('on:hairone-color-tour:start'));
    }

    function syncDisclosureState() {
      const active = disclosure ? disclosure.open : true;
      root.classList.toggle('is-tour-active', active);
      if (active) signalTourStart();
      if (!active) {
        checkout?.classList.remove('is-floating');
        root.classList.remove('has-floating-checkout');
      }
    }

    function selectedInputs(scope) {
      return quantityInputs.filter(input => (!scope || input.closest(`[data-ho-kind="${scope}"]`)) && clampQuantity(input.value) > 0);
    }

    function syncQuantity(input) {
      const quantity = clampQuantity(input.value);
      input.value = String(quantity);
      const item = input.closest('[data-ho-selectable-item]');
      const checkbox = item?.querySelector('[data-ho-select]');
      if (checkbox) checkbox.checked = quantity > 0;
      item?.classList.toggle('is-selected', quantity > 0);
      input.setAttribute('aria-valuenow', String(quantity));
      if (input.closest('[data-ho-kind="developer"]') && selectedInputs('developer').length === 0) developerWarningAccepted = false;
      updateSummary();
      updateNavigation();
    }

    function updateSummary() {
      const kinds = ['color', 'developer', 'accessory'];
      const labels = {
        color: ['Farbton', 'Farbtöne'],
        developer: ['Entwickler', 'Entwickler'],
        accessory: ['Zubehör', 'Zubehör']
      };
      const pieces = [];
      let totalCents = 0;
      for (const kind of kinds) {
        const inputs = selectedInputs(kind);
        const units = inputs.reduce((sum, input) => sum + clampQuantity(input.value), 0);
        const positions = inputs.length;
        if (positions) pieces.push(`${positions} ${labels[kind][positions === 1 ? 0 : 1]} · ${units} St.`);
        for (const input of inputs) totalCents += clampQuantity(input.value) * Number(input.dataset.price || 0);
      }
      summary.textContent = pieces.length ? pieces.join('  |  ') : 'Noch nichts ausgewählt';
      total.textContent = formatMoney(totalCents);
    }

    function updateNavigation() {
      const hasSelection = selectedInputs().length > 0;
      if (nextButton) {
        nextButton.disabled = currentStep === 3;
        nextButton.hidden = currentStep === 3;
      }
      if (backButton) {
        backButton.hidden = currentStep === 1;
        backButton.disabled = currentStep === 1;
      }
      if (addAllButton) {
        addAllButton.hidden = currentStep !== 3;
        addAllButton.disabled = !hasSelection;
      }
    }

    function hideDeveloperWarning() {
      if (developerWarning) developerWarning.hidden = true;
    }

    function scrollTourIntoView() {
      const stickyHeader = document.querySelector('.cc-header--sticky');
      const headerBottom = stickyHeader?.getBoundingClientRect().bottom || 0;
      const visualOffset = window.visualViewport?.offsetTop || 0;
      const targetTop = Math.max(0, window.scrollY + root.getBoundingClientRect().top - headerBottom - visualOffset - 12);
      const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: targetTop, behavior: coarsePointer || reducedMotion ? 'auto' : 'smooth' });
    }

    function showStep(step, options = {}) {
      const { focus = true, scroll = true } = options;
      currentStep = Math.max(1, Math.min(3, step));
      steps.forEach(panel => {
        const active = Number(panel.dataset.hoTourStep) === currentStep;
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });
      progressSteps.forEach(item => {
        const active = Number(item.dataset.hoProgressStep) === currentStep;
        if (active) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
        item.classList.toggle('is-complete', Number(item.dataset.hoProgressStep) < currentStep);
      });
      progress.textContent = `Schritt ${currentStep} von 3`;
      if (cartStatus) cartStatus.textContent = '';
      if (currentStep !== 2) hideDeveloperWarning();
      updateNavigation();
      if (focus) root.querySelector(`[data-ho-tour-step="${currentStep}"] h3`)?.focus({ preventScroll: true });
      if (scroll) requestAnimationFrame(scrollTourIntoView);
    }

    function requestStep(step) {
      const targetStep = Math.max(1, Math.min(3, step));
      if (targetStep === 3 && selectedInputs('developer').length === 0 && !developerWarningAccepted) {
        if (currentStep !== 2) showStep(2);
        if (developerWarning) {
          developerWarning.hidden = false;
          developerWarning.focus({ preventScroll: true });
          developerWarning.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        return;
      }
      showStep(targetStep);
    }

    function requestAdvance() {
      requestStep(currentStep + 1);
    }

    function showFamily(family) {
      currentFamily = family;
      familyButtons.forEach(button => {
        const active = button.dataset.hoColorFamily === family;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });
      familyGroups.forEach(group => {
        const active = group.dataset.hoColorGroup === family;
        group.hidden = !active;
        group.setAttribute('aria-hidden', String(!active));
        group.querySelectorAll('[data-ho-color-item]').forEach(item => { item.hidden = false; });
      });
      if (search) search.value = '';
      if (searchStatus) searchStatus.textContent = '';
    }

    familyButtons.forEach((button, index) => {
      button.addEventListener('click', () => showFamily(button.dataset.hoColorFamily));
      button.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let target = index;
        if (event.key === 'ArrowRight') target = (index + 1) % familyButtons.length;
        if (event.key === 'ArrowLeft') target = (index - 1 + familyButtons.length) % familyButtons.length;
        if (event.key === 'Home') target = 0;
        if (event.key === 'End') target = familyButtons.length - 1;
        familyButtons[target].focus();
        familyButtons[target].click();
      });
    });

    search?.addEventListener('input', () => {
      const query = search.value.trim().toLocaleLowerCase('de-DE');
      if (!query) {
        showFamily(currentFamily);
        return;
      }
      familyButtons.forEach(button => { button.setAttribute('aria-selected', 'false'); button.tabIndex = -1; });
      let matches = 0;
      familyGroups.forEach(group => {
        group.hidden = false;
        group.setAttribute('aria-hidden', 'false');
        let groupMatches = 0;
        group.querySelectorAll('[data-ho-color-item]').forEach(item => {
          const visible = (item.dataset.searchText || '').includes(query);
          item.hidden = !visible;
          if (visible) { matches += 1; groupMatches += 1; }
        });
        group.hidden = groupMatches === 0;
      });
      if (searchStatus) searchStatus.textContent = matches === 1 ? '1 Farbton gefunden' : `${matches} Farbtöne gefunden`;
    });

    root.addEventListener('click', event => {
      const quantityButton = event.target.closest('[data-ho-qty-plus],[data-ho-qty-minus]');
      if (quantityButton) {
        const item = quantityButton.closest('[data-ho-selectable-item]');
        const input = item?.querySelector('[data-ho-qty-input]');
        if (!input) return;
        input.value = String(clampQuantity(input.value) + (quantityButton.hasAttribute('data-ho-qty-plus') ? 1 : -1));
        syncQuantity(input);
      }
    });

    root.querySelectorAll('[data-ho-select]').forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const input = checkbox.closest('[data-ho-selectable-item]')?.querySelector('[data-ho-qty-input]');
        if (!input) return;
        input.value = checkbox.checked ? String(Math.max(1, clampQuantity(input.value))) : '0';
        syncQuantity(input);
      });
    });

    quantityInputs.forEach(input => {
      input.addEventListener('input', () => syncQuantity(input));
      input.addEventListener('change', () => syncQuantity(input));
      syncQuantity(input);
    });

    root.querySelectorAll('[data-ho-accessory-variant]').forEach(select => {
      select.addEventListener('change', () => {
        const input = select.closest('[data-ho-selectable-item]')?.querySelector('[data-ho-qty-input]');
        if (input) input.dataset.variantId = select.value;
      });
    });

    chooseSetButton?.addEventListener('click', () => {
      root.querySelectorAll('[data-ho-accessory-item] [data-ho-qty-input]').forEach(input => {
        input.value = '1';
        syncQuantity(input);
      });
      chooseSetButton.textContent = 'Set ausgewählt';
    });

    progressButtons.forEach(button => {
      button.addEventListener('click', () => requestStep(Number(button.dataset.hoGoStep)));
    });

    backButton?.addEventListener('click', () => showStep(currentStep - 1));
    nextButton?.addEventListener('click', () => {
      if (!nextButton.disabled) requestAdvance();
    });

    confirmNoDeveloperButton?.addEventListener('click', () => {
      developerWarningAccepted = true;
      hideDeveloperWarning();
      showStep(3);
    });

    returnDeveloperButton?.addEventListener('click', () => {
      hideDeveloperWarning();
      root.querySelector('[data-ho-developer-item] [data-ho-select]')?.focus({ preventScroll: true });
    });

    async function addSelectionToCart(trigger = addAllButton) {
      const aggregate = new Map();
      for (const input of quantityInputs) {
        const quantity = clampQuantity(input.value);
        const id = input.dataset.variantId;
        if (!id || quantity <= 0) continue;
        aggregate.set(id, (aggregate.get(id) || 0) + quantity);
      }
      const items = [...aggregate.entries()].map(([id, quantity]) => ({ id: Number(id), quantity }));
      if (!items.length) return false;
      if (trigger) {
        trigger.disabled = true;
        trigger.setAttribute('aria-busy', 'true');
      }
      if (addAllButton) addAllButton.disabled = true;
      cartStatus.textContent = 'Auswahl wird hinzugefügt …';
      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const sections = ['cart-icon-bubble'];
        const drawerSection = cartDrawer?.closest('.shopify-section')?.id.replace('shopify-section-', '');
        if (drawerSection) sections.push(drawerSection);
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(`Cart read failed: ${oldCartResponse.status}`);
        const oldCart = await oldCartResponse.json();
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || `Cart add failed: ${response.status}`);
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && data.sections?.['cart-icon-bubble']) {
          cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
        }
        if (cartDrawer && data.sections) {
          await cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
        }
        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(`Cart read failed: ${newCartResponse.status}`);
        const newCart = await newCartResponse.json();
        items.forEach(item => {
          const previous = oldCart.items.find(line => line.variant_id === item.id);
          root.dispatchEvent(new CustomEvent(previous ? 'on:line-item:change' : 'on:cart:add', {
            bubbles: true,
            detail: previous ? {
              cart: newCart,
              variantId: item.id,
              oldQuantity: previous.quantity,
              newQuantity: newCart.items.find(line => line.variant_id === item.id)?.quantity || previous.quantity
            } : { cart: newCart, variantId: item.id }
          }));
        });
        cartStatus.textContent = 'Auswahl wurde in den Warenkorb gelegt.';
        return true;
      } catch (error) {
        console.error('[hairOne color tour]', error);
        cartStatus.textContent = 'Das hat gerade nicht geklappt. Bitte versuche es erneut.';
        root.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: error.message } }));
        return false;
      } finally {
        if (trigger) {
          trigger.removeAttribute('aria-busy');
          if (trigger !== addAllButton) trigger.disabled = false;
        }
        updateNavigation();
      }
    }

    skipButtons.forEach(button => {
      button.addEventListener('click', async () => {
        const step = Number(button.dataset.hoSkipStep);
        if (step === 1 || step === 2) {
          requestAdvance();
          return;
        }
        if (step !== 3) return;
        root.querySelectorAll('[data-ho-accessory-item] [data-ho-qty-input]').forEach(input => {
          input.value = '0';
          syncQuantity(input);
        });
        if (selectedInputs().length > 0) {
          await addSelectionToCart(button);
        } else {
          cartStatus.textContent = 'Du hast keine Produkte ausgewählt. Du kannst direkt im Sortiment weiterstöbern.';
          cartStatus.focus({ preventScroll: true });
          document.querySelector('#filter-results')?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        }
      });
    });

    addAllButton?.addEventListener('click', () => addSelectionToCart(addAllButton));

    showFamily(currentFamily);
    showStep(1, { focus: false, scroll: false });
    updateSummary();

    let tourInViewport = false;
    const syncFloatingCheckout = () => {
      if (!checkout) return;
      const active = (disclosure ? disclosure.open : true) && tourInViewport;
      checkout.classList.toggle('is-floating', active);
      root.classList.toggle('has-floating-checkout', active);
    };
    if (checkout && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        tourInViewport = entries.some(entry => entry.isIntersecting);
        syncFloatingCheckout();
      }, { rootMargin: '-72px 0px -72px 0px' });
      observer.observe(root);
    }

    disclosure?.addEventListener('toggle', () => {
      syncDisclosureState();
      syncFloatingCheckout();
    });

    const deepLinked = new URLSearchParams(window.location.search).get('color_tour') === '1';
    if (deepLinked && disclosure) {
      disclosure.open = true;
      requestAnimationFrame(scrollTourIntoView);
    }
    syncDisclosureState();
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-ho-color-tour]').forEach(initColorTour);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
